/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
*  Copyright © 2022 Pearson Education, Inc.
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Pearson Education, Inc.  The intellectual and technical concepts contained
* herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
* patent applications, and are protected by trade secret or copyright law.
* Dissemination of this information, reproduction of this material, and copying or distribution of this software
* is strictly forbidden unless prior written permission is obtained
* from Pearson Education, Inc.
* */

/**
 * QR code popup style
 *
 * @file QRCodeStyles.jsx
 * @author Ujjwal Sana
 * @since 0.0.1
 */

import { styled } from '@mui/material/styles';
import {
  Box, DialogActions, DialogContent, IconButton, Typography, Button
} from '@mui/material';
import mixins from '../../../../assets/styles/mixins.scss';
import CommonStyles from '../../../../assets/styles/CommonStyles';

export const DialogPaper = styled('div')({
  '&&': {
    alignItems: 'center',
    padding: 24,
    borderRadius: 16,
    boxShadow: '0px 0px 32px 0px #020917 !important',
    background: `var(--lightBgColor,${mixins.$whiteFont})`,
    '&:focus-visible': {
      background: mixins.qrWhiteLightFont,
      outline: 'none',
      border: `2px solid var(--secondaryButtonFocusColor,${mixins.swatchDarkFont})`,
      boxShadow: `0px 0px 0px 2px ${mixins.qrWhiteLightFont}`
    },
    maxWidth: '100%',
    maxHeight: 'calc(100% - 64px)',
    '@media screen and (min-width: 768px)': {
      width: 480,
      // Keep the designed height only while the viewport can fit it (zoom/short screens)
      minHeight: 'min(648px, calc(100vh - 64px))'
    },
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      minWidth: 'min(344px, 100%)',
      minHeight: 'min(612px, calc(100vh - 64px))',
      padding: 16
    }
  }
});

export const DialogContentContainer = styled(DialogContent)({
  '&&': {
    order: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    marginTop: 32,
    padding: '0px !important',
    // Take the space left by the header and scroll when zoomed in, so no content is cut off
    flex: '1 1 auto',
    minHeight: 0,
    maxWidth: '100%',
    ...CommonStyles().scrollContainer({}),
    '@media screen and (min-width: 768px)': {
      width: 432
    },
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      width: 312,
      marginTop: 16
    }
  }
});

export const DialogHeader = styled(Typography)({
  '&&': {
    fontFamily: `var(--fontFamily,${mixins.fontFamilyTT})`,
    fontWeight: `var(--fontWeight400,${mixins.fontWeight400})`,
    fontSize: `var(--fontSize32,${mixins.fontSize24})`,
    lineHeight: `var(--lineHeight40,${mixins.lineHeight28})`,
    color: `var(--color_text_primary,${mixins.darkThemeColor})`,
    letterSpacing: 'var(--letterSpacing64, 0.4px)',
    textAlign: 'left',

    '@media screen and (min-width: 768px)': {
      maxWidth: 368
    },
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      maxWidth: 248,
      fontSize: `var(--fontSize24,${mixins.fontSize20})`,
      lineHeight: `var(--lineHeight30,${mixins.lineHeight24})`,
      letterSpacing: 'var(--letterSpacing48)'
    }
  }
});

export const DialogAction = styled(DialogActions)({
  '&&': {
    padding: 0,
    margin: 0,
    width: 40,
    height: 40
  }
});

export const CloseIconButton = styled(IconButton)({
  '&&': {
    width: 44,
    height: 44
  }
});

const dialogQRSubheaderTypography = {
  color: `var(--color_text_primary, ${mixins.darkThemeColor})`,
  minHeight: 48,
  maxWidth: '100%',
  fontSize: `var(--fontSize20, ${mixins.fontSize20})`,
  textAlign: 'center',
  wordBreak: 'break-word',
  fontFamily: `var(--fontFamily, ${mixins.fontFamilyTT})`,
  fontWeight: `var(--fontWeight400, ${mixins.fontWeight400})`,
  lineHeight: `var(--lineHeight28, ${mixins.lineHeight24})`,
  letterSpacing: `var(--letterSpacing4, ${mixins.letterSpacing4})`
};

export const DialogQRSubheader = styled(Typography)({
  '&&': {
    ...dialogQRSubheaderTypography,
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      width: 312,
      maxWidth: '100%',
      minHeight: 72
    }
  },
  '&&&.MuiTypography-body1': dialogQRSubheaderTypography
});

export const InfoDescriptionWrapper = styled(Box)({
  '&&': {
    gap: 32,
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      gap: 16
    }
  }
});

export const QrImageContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexShrink: 0,
    maxWidth: '100%',
    padding: 32,
    gap: 32,
    borderRadius: 12,
    border: '2px solid var(--skeletonColor, #C7CCD6)'
  }
});

export const QrErrorContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    gap: 16,
    borderRadius: 12,
    border: `2px solid var(--skeletonColor,${mixins.qrErrorMsg})`,
    minHeight: 232,
    flexShrink: 0,
    maxWidth: '100%'
  }
});

export const LoaderContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    minWidth: 200
  }
});

export const DialogErrorMsg = styled('div')({
  '&&': {
    display: 'flex',
    fontFamily: `var(--fontFamily,${mixins.fontFamilyTT})`,
    fontWeight: mixins.fontWeight700,
    fontSize: `var(--fontSize14,${mixins.fontSize20})`,
    lineHeight: `var(--lineHeight22,${mixins.lineHeight24})`,
    textAlign: 'center',
    letterSpacing: '0.4px',
    color: `var(--color_text_primary,${mixins.qrErrorMsg})`,
    wordBreak: 'break-word',
    width: '100%',
    maxWidth: 232,
    justifyContent: 'center',
    alignItems: 'center'
  }
});

export const CloseContainer = styled(Button)({
  '&&': {
    padding: '6px 24px 6px 24px',
    border: `2px solid var(--purpleSecondaryText, ${mixins.swatchDark800})`,
    borderRadius: 40,
    background: `var(--lightBgColor,${mixins.swatchDark800})`,
    width: 'fit-content',
    cursor: 'pointer',
    '& span': {
      minWidth: 'unset'
    },
    '&:hover': {
      background: `var(--lightBgColor1,${mixins.darkBgBorder})`,
      border: `2px solid var(--purpleSecondaryText,${mixins.darkBgBorder})`
    },
    '&:focus-visible': {
      background: `var(--lightBgColor1,${mixins.darkBgBorder})`,
      border: `2px solid var(--secondaryButtonFocusColor, ${mixins.greyColor})`,
      boxShadow: `0px 0px 0px 2px var(--lightBgColor1,${mixins.darkBgBorder})`,
      '&::after': {
        border: 'none'
      }
    }
  }
});

export const CloseButtonText = styled('div')({
  '&&': {
    fontFamily: `var(--fontFamily,${mixins.fontFamilyTT})`,
    fontWeight: `var(--fontWeight500,${mixins.fontWeight700})`,
    fontSize: `var(--fontSize14,${mixins.fontSize18})`,
    lineHeight: `var(--lineHeight16,${mixins.lineHeight24})`,
    letterSpacing: 'var(--letterSpacing0, 0.4px)',
    color: `var(--purpleSecondaryText,${mixins.whiteFont})`
  }
});

export const QrMsgContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    maxWidth: '100%',
    '@media screen and (min-width: 768px)': {
      width: 432,
      maxWidth: '100%',
      minHeight: 80
    }
  }
});

const dialogQueMsgTypography = {
  color: `var(--grayScaleTextSecondary, ${mixins.darkThemeColor})`,
  fontSize: `var(--fontSize18, ${mixins.fontSize18})`,
  textAlign: 'center',
  wordBreak: 'break-word',
  fontFamily: `var(--fontFamily, ${mixins.fontFamilyTT})`,
  fontWeight: `var(--fontWeight400, ${mixins.fontWeight700})`,
  lineHeight: `var(--lineHeight28, ${mixins.lineHeight24})`,
  letterSpacing: 'var(--letterSpacing0, 0.24px)'
};

export const DialogQueMsg = styled(Typography)({
  '&&': dialogQueMsgTypography,
  '&&&.MuiTypography-body1': dialogQueMsgTypography
});

const dialogFooterMsgTypography = {
  color: `var(--grayScaleTextSecondary, ${mixins.darkThemeColor})`,
  fontSize: `var(--fontSize14, ${mixins.fontSize16})`,
  textAlign: 'center',
  wordBreak: 'break-word',
  fontFamily: `var(--fontFamily, ${mixins.fontFamilyTT})`,
  fontWeight: mixins.fontWeight400,
  lineHeight: `var(--lineHeight22, ${mixins.lineHeight20})`,
  letterSpacing: 'var(--letterSpacing0, 0.24px)'
};

export const DialogFooterMsg = styled(Typography)({
  '&&': dialogFooterMsgTypography,
  '&&&.MuiTypography-body1': dialogFooterMsgTypography
});

export const DialogFooterAction = styled(Typography)({
  '&&': {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    width: 432,
    maxWidth: '100%',
    minHeight: 40,
    gap: 8,
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      width: 312,
      maxWidth: '100%',
      minHeight: 40
    }
  }
});

export const DialogTitleInfoContanier = styled('div')({
  '&&': {
    order: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: 24,
    flexShrink: 0,
    maxWidth: '100%',

    '@media screen and (min-width: 768px)': {
      width: 432,
      maxWidth: '100%',
      minHeight: 40
    },
    '@media screen and (min-width: 360px) and (max-width: 767px)': {
      width: 312,
      maxWidth: '100%',
      minHeight: 40
    }
  }
});
